import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  PackageIcon,
  PackagePlusIcon,
  PackageCheckIcon,
  PlusIcon,
  ArrowUpRightIcon,
  DownloadIcon,
} from 'lucide-react'

import {
  Box,
  Grid,
  Stack,
  Typography,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material'

import { Card, Button, Skeleton } from '@repo/ui'

import { ORDER_STATUSES, PAYMENT_STATUSES } from 'constants/statuses'
import { datetime, toDollar } from 'utils'
import { ROUTES } from 'routes'

import {
  Header,
  StatusChipCell,
  CompanyCell,
  AddressCell,
  UserCell,
  EmptyState,
  LinkPackageModal,
  TagChip,
  ProductCell,
  // Shared label/value primitives — also used by InternalInvoicePage so the two
  // internal detail surfaces can't drift (working-conventions §36).
  DetailField as Field,
  DetailSummaryRow as SummaryRow,
} from 'components'

import { useOrder, useFulfillShipment, useBuyShipmentLabel, useCreateShipment, useRemoveShipmentProduct, useOrderDocumentDownload } from 'hooks/api/useShopifyOrdersQuery'
import { useInternalInvoices } from 'hooks/api'
import { useReferrerState } from 'hooks'
import { useSnackbar } from 'contexts'
import { ShipmentCard, BuyLabelConfirmModal, CreateShipmentModal } from './components'

// VRG-138 (VERA-891): the order-level status is a SINGLE progression derived
// from its shipments (each package ships independently) — Unfulfilled → Shipped
// → In Transit → Delivered. The order stays Unfulfilled until every package has
// shipped; it only reaches Delivered when all have. With no shipments yet, fall
// back to Shopify's order-level status.
const deriveOrderStatus = (
  shipments: Array<{ status: string }> | undefined,
  fallback: string,
): string => {
  const statuses = shipments?.map(s => s.status) ?? []
  if (statuses.length === 0) return fallback
  if (statuses.some(s => s === 'ready')) return 'unfulfilled'
  if (statuses.every(s => s === 'delivered')) return 'delivered'
  if (statuses.every(s => s === 'delivered' || s === 'in_transit')) return 'in_transit'
  return 'shipped'
}

const OrderDetailSkeleton = () => (
  <Grid container spacing={4}>
    <Grid item xs={12} md={8}>
      <Stack spacing={4}>
        {/* Details */}
        <Card>
          <Stack spacing={4}>
            <Skeleton height={20} width="20%" />
            <Skeleton lines={4} />
          </Stack>
        </Card>
        {/* Fulfillment */}
        <Card>
          <Stack spacing={4}>
            <Skeleton height={20} width="30%" />
            <Skeleton height={64} />
            <Skeleton lines={4} />
          </Stack>
        </Card>
        {/* Products */}
        <Card>
          <Stack spacing={3}>
            <Skeleton height={20} width="25%" />
            <Skeleton lines={3} />
          </Stack>
        </Card>
      </Stack>
    </Grid>
    <Grid item xs={12} md={4}>
      <Card>
        <Stack spacing={3}>
          <Skeleton height={20} width="50%" />
          <Skeleton lines={3} />
        </Stack>
      </Card>
    </Grid>
  </Grid>
)

export const OrderDetailPage = () => {
  const { id = '' } = useParams<{ id: string }>()
  const theme = useTheme()
  const navigate = useNavigate()
  const { open: notify } = useSnackbar()
  // Detail → detail: the derived label would be the Orders list, not this order.
  const referrer = useReferrerState('Order')

  const { data: order, isLoading, refetch } = useOrder(id)

  // VRG-138 REDESIGN: assign a package PER PRODUCT. The "Assign Package" button
  // on a product row opens LinkPackageModal in scan mode — tap an existing
  // package's handle to resolve + assign it to that one line item.
  const [assignLineItemId, setAssignLineItemId] = useState<string | null>(null)

  // "Create shipment" — group products (each with a package) into one shipment
  // + box. `preselect` seeds the modal's selection (single-product = the lone item).
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false)
  const [createShipmentPreselect, setCreateShipmentPreselect] = useState<string[]>([])

  // VRG-138 REDESIGN: fulfill + buy-label act on ONE shipment (by id) at a time;
  // `activeShipmentId` scopes the loading state to the shipment being acted on.
  const [activeShipmentId, setActiveShipmentId] = useState<string | null>(null)
  const [labelConfirmShipmentId, setLabelConfirmShipmentId] = useState<string | null>(null)

  // VERA-891 — resolving "the invoice for this order", in precedence order:
  //   1. a Vera-issued invoice whose Stripe metadata order_id points at this
  //      order (the real internal invoice — links to /internal/invoices/:id and
  //      enables Download). None exist yet: at the time of writing the only
  //      order-typed invoice carries a demo id (`ord_VERA644_demo_0001`) that
  //      matches no Shopify order, so this lights up on its own once invoices
  //      are created carrying the order id — no further code change needed.
  //   2. Shopify's own `DraftOrder.invoiceUrl` — the customer-facing invoice /
  //      checkout link Shopify emails. Real today, so the button works now.
  //   3. neither → the button stays disabled with a tooltip.
  // Match tolerantly: the metadata could carry the GID, the bare numeric id, or
  // the order name with or without its leading '#'.
  const { data: invoiceList } = useInternalInvoices({ limit: 100 })
  const veraInvoice = useMemo(() => {
    if (!order) return undefined
    const numericId = String(order.id).split('/').pop() ?? ''
    const candidates = new Set(
      [order.id, numericId, order.name, order.name?.replace(/^#/, '')]
        .filter(Boolean)
        .map(v => String(v).toLowerCase()),
    )
    return invoiceList?.invoices?.find(
      inv => inv.orderId && candidates.has(inv.orderId.toLowerCase()),
    )
  }, [invoiceList, order])

  const shopifyInvoiceUrl = order?.invoiceUrl ?? null

  // The Vera-branded order document (PDF) — always available, since Vera owns
  // the order data outright (unlike the invoice, which needs Stripe).
  const downloadOrderDocument = useOrderDocumentDownload()
  const handleDownloadOrderDocument = () => {
    if (!order) return
    downloadOrderDocument.mutate(
      { orderId: order.id, orderName: order.name },
      {
        onError: () =>
          notify({ type: 'error', text: 'Unable to download this order. Please try again.' }),
      },
    )
  }

  const fulfillShipment = useFulfillShipment()
  const buyShipmentLabel = useBuyShipmentLabel()
  const removeShipmentProduct = useRemoveShipmentProduct()
  const [removingLineItemId, setRemovingLineItemId] = useState<string | null>(null)

  const handleRemoveProduct = (shipmentId: string, lineItemId: string) => {
    setRemovingLineItemId(lineItemId)
    removeShipmentProduct.mutate(
      { orderId: id, shipmentId, lineItemId },
      { onSettled: () => setRemovingLineItemId(null) },
    )
  }

  const shipments = order?.shipments ?? []
  // Goods total for the Summary card, before the shipping charge. Shopify's
  // totalPriceSet is the grand total, so the subtotal is derived from the line
  // items rather than read off the order.
  const subtotal = (order?.lineItems ?? []).reduce(
    (sum, item) => sum + parseFloat(item.variant?.price || '0') * item.quantity,
    0,
  )
  // Line items already grouped into a shipment. Those move into their shipment
  // card (self-documenting box → contents), so the flat Products list below
  // shows only the products NOT yet in a shipment (the staging area).
  const shippedLineItemIds = new Set(shipments.flatMap(s => s.lineItemIds))
  const productsToShip = (order?.lineItems ?? []).filter(item => !shippedLineItemIds.has(item.id))
  // Products with a package assigned but not yet in a shipment — eligible to
  // create a shipment from.
  const eligibleProducts = (order?.lineItems ?? [])
    .filter(item => (item.linkedPackages?.length ?? 0) > 0 && !shippedLineItemIds.has(item.id))
    .map(item => ({
      id: item.id,
      title: item.title,
      image: item.image,
      sku: item.variant?.sku,
      packageName: item.linkedPackages?.[0]?.name,
      // Number of Vera packages assigned to this product — drives the summed
      // shipment weight (typically 1 per product).
      packageCount: item.linkedPackages?.length ?? 1,
    }))

  const openAssign = (lineItemId: string) => setAssignLineItemId(lineItemId)
  const closeAssign = () => setAssignLineItemId(null)

  const createShipment = useCreateShipment()
  const openCreateShipment = () => {
    // Single-product order: the box IS the product's own package (per-variant
    // Shopify package), so there's no box to choose — create directly. Multi-
    // product: open the modal to select products + pick a shipment box size.
    if (eligibleProducts.length <= 1) {
      createShipment.mutate({ orderId: id, lineItemIds: eligibleProducts.map(p => p.id) })
    } else {
      setCreateShipmentPreselect([])
      setCreateShipmentOpen(true)
    }
  }
  const handleShipmentCreated = () => {
    setCreateShipmentOpen(false)
    setCreateShipmentPreselect([])
  }

  const handleFulfill = (shipmentId: string) => {
    setActiveShipmentId(shipmentId)
    fulfillShipment.mutate(
      { orderId: id, shipmentId },
      { onSettled: () => setActiveShipmentId(null) },
    )
  }

  const openBuyLabelConfirm = (shipmentId: string) => setLabelConfirmShipmentId(shipmentId)
  const closeBuyLabelConfirm = () => setLabelConfirmShipmentId(null)

  const confirmBuyLabel = () => {
    if (!labelConfirmShipmentId) return
    const shipmentId = labelConfirmShipmentId
    setActiveShipmentId(shipmentId)
    buyShipmentLabel.mutate(
      { orderId: id, shipmentId },
      { onSettled: () => setActiveShipmentId(null) },
    )
    setLabelConfirmShipmentId(null)
  }

  const labelConfirmShipment = shipments.find(s => s.id === labelConfirmShipmentId)

  // Fulfillment header (title + Create shipment) — shared by the single- and
  // multi-shipment layouts.
  const fulfillmentHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
      <Typography variant="body1" fontWeight="medium">
        Fulfillment
      </Typography>
      {eligibleProducts.length > 0 && (
        <Button
          disableElevation
          variant="primary"
          loading={createShipment.isPending}
          startIcon={<PlusIcon strokeWidth={1} size={14} />}
          onClick={openCreateShipment}
        >
          Create shipment
        </Button>
      )}
    </Stack>
  )

  const renderShipmentCards = (flush = false) =>
    shipments.map(shipment => {
      // Distinct line items in this shipment (a product may appear once per
      // assigned package — dedupe to one row).
      const products = [...new Set(shipment.lineItemIds)]
        .map(lineItemId => (order?.lineItems ?? []).find(item => item.id === lineItemId))
        .filter((item): item is NonNullable<typeof item> => !!item)

      return (
        <ShipmentCard
          key={shipment.id}
          shipment={shipment}
          products={products}
          onFulfill={() => handleFulfill(shipment.id)}
          onBuyLabel={() => openBuyLabelConfirm(shipment.id)}
          onRemoveProduct={lineItemId => handleRemoveProduct(shipment.id, lineItemId)}
          removingLineItemId={removingLineItemId}
          isFulfilling={activeShipmentId === shipment.id && fulfillShipment.isPending}
          isBuyingLabel={activeShipmentId === shipment.id && buyShipmentLabel.isPending}
          flush={flush}
        />
      )
    })

  return (
    <>
      <Header
        title={order ? `Order ${order.name}` : 'Order'}
        breadcrumbsOveride={order ? order.name : 'Order'}
        status={order ? { statuses: ORDER_STATUSES, value: deriveOrderStatus(order.shipments, order.fulfillmentStatus) } : undefined}
        rightComponents={
          /* Mirrors the internal Invoice page's top bar ("View order" +
             "Download"). See the invoice-resolution comment above for the
             Vera-invoice → Shopify-invoiceUrl → disabled precedence. */
          <Stack direction="row" spacing={3} alignItems="center">
            <Tooltip
              title={
                veraInvoice
                  ? 'Open this order’s Vera invoice'
                  : shopifyInvoiceUrl
                    ? 'Open the Shopify invoice for this order'
                    : 'No invoice for this order yet'
              }
              placement="bottom"
            >
              <Box>
                <Button
                  variant="secondary"
                  disableElevation
                  disabled={!veraInvoice && !shopifyInvoiceUrl}
                  startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                  onClick={() => {
                    if (veraInvoice) {
                      navigate(ROUTES.INTERNAL_INVOICE(veraInvoice.id), { state: referrer })
                    } else if (shopifyInvoiceUrl) {
                      window.open(shopifyInvoiceUrl, '_blank', 'noopener,noreferrer')
                    }
                  }}
                >
                  View invoice
                </Button>
              </Box>
            </Tooltip>
            {/* Downloads the Vera-branded ORDER document, not the invoice —
                this is the Order page, and the order document is always
                available (Vera owns that data). The invoice, when one exists,
                is reachable via "View invoice". */}
            <Tooltip title="Download the Vera order PDF" placement="bottom">
              <Box>
                <Button
                  variant="secondary"
                  disableElevation
                  loading={downloadOrderDocument.isPending}
                  startIcon={<DownloadIcon strokeWidth={1} />}
                  onClick={handleDownloadOrderDocument}
                >
                  Download
                </Button>
              </Box>
            </Tooltip>
          </Stack>
        }
      />

      {isLoading ? (
        <OrderDetailSkeleton />
      ) : !order ? (
        <EmptyState
          title="Order not found"
          text="This order could not be loaded from Shopify. It may have been removed, or there was a connection issue."
          icon={<PackageIcon strokeWidth={1} size={32} />}
        />
      ) : (
        <Grid container spacing={4}>
          {/* Left column — details, fulfillment, line items */}
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              {/* Details — the order's metadata, matching the internal Invoice
                  page's Details card: same two-column row-major grid so the two
                  internal detail surfaces read identically. "Summary" is
                  reserved for money (right column), never metadata. */}
              <Card>
                <Stack spacing={6}>
                  <Typography variant="body1" fontWeight="medium">
                    Details
                  </Typography>

                  {/* Each field is its own grid cell flowing row-major, so the
                      columns stay aligned row-by-row (independent column Stacks
                      drift when one side holds taller avatar rows). */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      alignItems: 'start',
                      columnGap: 6,
                      rowGap: 6,
                    }}
                  >
                    {/* Brand / company — the internal-specific field */}
                    <Field label="Company">
                      {order.company ? (
                        <CompanyCell
                          company={{ id: order.company.id, name: order.company.name, logo: '' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Unmatched
                        </Typography>
                      )}
                    </Field>

                    <Field label="Placed by">
                      <UserCell
                        user={{
                          first_name: order.customer.firstName,
                          last_name: order.customer.lastName,
                          email: order.customer.email,
                        }}
                        route={ROUTES.INTERNAL_USERS}
                      />
                    </Field>

                    <Field label="Created">{datetime(order.createdAt)}</Field>

                    <Field label="Payment status">
                      <StatusChipCell status={order.financialStatus} statuses={PAYMENT_STATUSES} />
                    </Field>

                    <Field label="Shipping address">
                      {order.shippingAddress?.address1 ? (
                        <AddressCell
                          address={{
                            address: order.shippingAddress.address1,
                            city: order.shippingAddress.city,
                            country: order.shippingAddress.country,
                            zip: order.shippingAddress.zip,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No shipping address
                        </Typography>
                      )}
                    </Field>
                  </Box>
                </Stack>
              </Card>

              {/* Fulfillment — always a Card (title + Create shipment in the
                  header, parity with Products). A SINGLE shipment renders flush
                  inside this card (no card-in-a-card) and drops the "N of M
                  shipped" rollup; 2+ shipments get the rollup + their own boxes. */}
              <Card>
                <Stack spacing={5}>
                  {fulfillmentHeader}
                  {shipments.length === 0 ? (
                    <Typography variant="body3" color="text.secondary">
                      {eligibleProducts.length > 0
                        ? 'Ready to ship. Create a shipment to group the assigned products into a box and buy a label.'
                        : 'No shipments yet. Assign a package to each product below, then create a shipment.'}
                    </Typography>
                  ) : shipments.length === 1 ? (
                    renderShipmentCards(true)
                  ) : (
                    <Stack spacing={3}>
                      <Typography variant="body3" color="text.secondary">
                        {shipments.filter(s => s.status !== 'ready').length} of {shipments.length} shipped
                      </Typography>
                      <Stack spacing={3}>{renderShipmentCards()}</Stack>
                    </Stack>
                  )}
                </Stack>
              </Card>

              {/* Products — assign a Vera package (box of tags) to each product. */}
              <Card>
                <Stack spacing={4}>
                  <Typography variant="body1" fontWeight="medium">
                    Products
                  </Typography>

                  {/* Legacy order-level links (bulk "Link Packages" header action,
                      not scoped to a product) — shown separately. */}
                  {order.orderLevelPackages && order.orderLevelPackages.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="body3" color="text.secondary">
                        Linked to order
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {order.orderLevelPackages.map(pkg => (
                          <TagChip
                            key={pkg.id}
                            icon={<PackageIcon size={14} strokeWidth={1} />}
                            label={pkg.name}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  )}

                  {productsToShip.length === 0 ? (
                    <Typography variant="body3" color="text.secondary">
                      All products are in a shipment.
                    </Typography>
                  ) : (
                    <Stack divider={<Divider />} spacing={3}>
                      {productsToShip.map(item => {
                        const packageAssigned = (item.linkedPackages?.length ?? 0) > 0

                        return (
                          <Stack key={item.id} direction="row" alignItems="center" spacing={3}>
                            {/* Product preview grows to fill; "Package assigned" is
                                right-aligned next to qty/price (the product IS the
                                package, so no package-name chip). */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <ProductCell
                                product={{
                                  title: item.title,
                                  sku: item.variant?.sku,
                                  images: item.image ? [item.image] : [],
                                }}
                                size={48}
                              />
                              {/* §36: buyer's customization surfaced for fulfillment —
                                  the uploaded logo(s) + finish/color the internal view
                                  previously dropped. */}
                              {item.customization?.frontLogoUrl && (
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                  sx={{ mt: 1.5, ml: '60px' }}
                                >
                                  {[item.customization.frontLogoUrl, item.customization.backLogoUrl]
                                    .filter(Boolean)
                                    .map((url, i) => (
                                      <Box
                                        key={i}
                                        component="img"
                                        src={url as string}
                                        alt={i === 0 ? 'Front logo' : 'Back logo'}
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 1,
                                          objectFit: 'contain',
                                          border: theme => `1px solid ${theme.palette.divider}`,
                                          bgcolor: 'background.default',
                                          p: 0.5,
                                        }}
                                      />
                                    ))}
                                  <Typography variant="body4" color="text.secondary">
                                    {['Custom logo',
                                      item.customization.logoFinish && `${item.customization.logoFinish} finish`,
                                      item.customization.color]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </Typography>
                                </Stack>
                              )}
                            </Box>
                            {packageAssigned && (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                <PackageCheckIcon strokeWidth={1} size={16} color={theme.palette.success.main} />
                                <Typography variant="body3" sx={{ color: 'success.main' }}>
                                  Package assigned
                                </Typography>
                              </Stack>
                            )}
                            <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                              <Typography variant="body3" color="text.secondary">
                                Qty {item.quantity}
                              </Typography>
                              <Typography variant="body2">
                                {toDollar(parseFloat(item.variant?.price || '0') * item.quantity)}
                              </Typography>
                            </Stack>
                            {!packageAssigned && (
                              <Box sx={{ flexShrink: 0 }}>
                                <Button
                                  disableElevation
                                  variant="secondary"
                                  startIcon={<PackagePlusIcon strokeWidth={1} size={14} />}
                                  onClick={() => openAssign(item.id)}
                                >
                                  Assign Package
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        )
                      })}
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Grid>

          {/* Right column — Summary = MONEY only (metadata lives in the
              Details card on the left), mirroring the internal Invoice page's
              Summary. Deliberately does NOT re-list products: they already
              appear in the Fulfillment shipments and the Products card, so a
              third listing would be noise. */}
          <Grid item xs={12} md={4}>
            <Card>
              <Stack spacing={6}>
                <Typography variant="body1" fontWeight="medium">
                  Summary
                </Typography>

                <Stack spacing={3}>
                  <SummaryRow label="Subtotal">{toDollar(subtotal)}</SummaryRow>

                  {/* VERA-891: the customer-facing shipping charge — Shopify's
                      own totalShippingPriceSet (set at checkout/draft-order
                      creation), NOT what Vera pays for the physical label
                      (Shopify's label-purchase API doesn't expose that — see
                      ShopifyServiceProvider.purchaseShippingLabel's doc
                      comment). Printing a label lives in the Fulfillment card. */}
                  {order.shippingPriceSet?.shopMoney?.amount != null && (
                    <SummaryRow label="Shipping price">
                      {toDollar(parseFloat(order.shippingPriceSet.shopMoney.amount))}
                    </SummaryRow>
                  )}
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {toDollar(parseFloat(order.totalPriceSet.shopMoney.amount))}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Per-product "Assign Package" — scan-only RESOLVE: tap an existing
          package's handle on the reader; LinkPackageModal (scanMode) READS the
          tag (doesn't rewrite it), resolves that package, and links it to this
          product. No manual list — the tabs are Reader/Phone, both scan. */}
      <LinkPackageModal
        order={order ?? null}
        open={!!assignLineItemId}
        onClose={closeAssign}
        refetch={refetch}
        lineItemIds={assignLineItemId ? [assignLineItemId] : []}
        singleSelect
        title="Assign package"
      />

      {/* Create a shipment from the products that have a package assigned. */}
      <CreateShipmentModal
        open={createShipmentOpen}
        onClose={() => setCreateShipmentOpen(false)}
        orderId={id}
        eligibleProducts={eligibleProducts}
        preselectedLineItemIds={createShipmentPreselect}
        onCreated={handleShipmentCreated}
      />

      <BuyLabelConfirmModal
        open={!!labelConfirmShipmentId}
        onClose={closeBuyLabelConfirm}
        onConfirm={confirmBuyLabel}
        loading={buyShipmentLabel.isPending}
        packageName={
          labelConfirmShipment?.packages?.map(p => p.name).join(', ') || undefined
        }
      />
    </>
  )
}
